import { type BufferGeometry, Mesh, MeshStandardMaterial, Vector3 } from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { makeSurfPrism } from './ramps';

export interface LaneSegment {
  length: number;
  /** Heading change applied BEFORE this segment, degrees (+ = turn left). */
  turnDeg?: number;
  /** Decline override for this segment, degrees. */
  declineDeg?: number;
}

export interface LaneSpec {
  width: number;
  angleDeg: number;
  declineDeg: number;
  segments: LaneSegment[];
}

/** Joint overlap so yawed segments never leave cracks (capsule radius is 16). */
const JOINT_OVERLAP = 32;

/**
 * Max heading change per physical segment. clipVelocity costs speed ~ with the
 * square of each joint's normal change, so curves are auto-subdivided to stay
 * smooth (Hammer mappers use 2.5-5° per brush segment for the same reason).
 */
const MAX_TURN_PER_SEGMENT = 3;

function subdivide(segments: LaneSegment[]): LaneSegment[] {
  const out: LaneSegment[] = [];
  for (const segment of segments) {
    const turn = segment.turnDeg ?? 0;
    const steps = Math.max(1, Math.ceil(Math.abs(turn) / MAX_TURN_PER_SEGMENT));
    for (let i = 0; i < steps; i++) {
      out.push({
        length: segment.length / steps,
        turnDeg: turn / steps,
        declineDeg: segment.declineDeg,
      });
    }
  }
  return out;
}

/**
 * Chains surf prisms into a continuous lane that can turn and vary decline —
 * the procedural equivalent of segmented curved ramps in Hammer (Momentum Mod
 * mapping docs recommend 2.5-5° steps per segment for smooth curves).
 * Returns the meshes; the caller adds them to scene/colliders. The returned
 * cursor/heading let lanes be chained across gaps.
 */
export function buildLane(
  spec: LaneSpec,
  start: Vector3,
  headingDeg: number,
  material: MeshStandardMaterial,
): { meshes: Mesh[]; cursor: Vector3; headingDeg: number } {
  const meshes: Mesh[] = [];
  const cursor = start.clone();
  let heading = headingDeg;

  for (const segment of subdivide(spec.segments)) {
    heading += segment.turnDeg ?? 0;
    const decline = segment.declineDeg ?? spec.declineDeg;
    const h = (heading * Math.PI) / 180;
    const d = (decline * Math.PI) / 180;

    const mesh = new Mesh(
      makeSurfPrism({
        length: segment.length + JOINT_OVERLAP,
        width: spec.width,
        angleDeg: spec.angleDeg,
        // Capless: interior caps protrude through neighbouring faces on the
        // inside of turns as backward walls that dead-stop riders.
        caps: false,
      }),
      material,
    );
    mesh.rotation.order = 'YXZ';
    mesh.rotation.y = h;
    mesh.rotation.x = d;
    mesh.position.copy(cursor);
    meshes.push(mesh);

    cursor.x += Math.sin(h) * segment.length * Math.cos(d);
    cursor.z += Math.cos(h) * segment.length * Math.cos(d);
    cursor.y -= segment.length * Math.sin(d);
  }

  return { meshes, cursor, headingDeg: heading };
}

const LANE_GEO_MATERIAL = new MeshStandardMaterial();

/**
 * Bakes a whole curved/turning lane into ONE local-space BufferGeometry — the
 * map editor places these as single curve shapes. Builds the lane at the origin
 * heading 0, folds each segment's transform into its geometry, and merges. The
 * result rides identically to the chained meshes the game uses for its turns.
 */
export function laneGeometry(spec: LaneSpec): BufferGeometry {
  const { meshes } = buildLane(spec, new Vector3(0, 0, 0), 0, LANE_GEO_MATERIAL);
  const geometries = meshes.map((mesh) => {
    mesh.updateMatrix();
    return mesh.geometry.clone().applyMatrix4(mesh.matrix);
  });
  const merged = mergeGeometries(geometries, false);
  if (!merged) {
    throw new Error('laneGeometry: merge failed (inconsistent segment attributes)');
  }
  return merged;
}
